import type {Canvas, FabricObject, Point} from 'fabric';
import {UnknownObject} from '../../types.ts';

export type TextAlign = 'left' | 'center' | 'right';

export interface FontSettings {
    bold: boolean;
    italic: boolean;
    font: string | undefined;
    fontSize?: number | undefined;
    align?: TextAlign;
}

export interface CanvasTransform {
    scale: number;
    translationX: number;
    translationY: number;
}

export interface CustomCanvas extends Canvas {
    isDragging?: boolean;
    lastPosX?: number;
    lastPosY?: number;
}

export interface CanvasState {
    version: string;
    objects: FabricObject[];
}

export interface CustomFabricObject extends FabricObject {
    elementId?: string;
    ordinalPosition?: number;
    uri?: string;
    videoElement?: HTMLVideoElement;
    meta?: string | null;
    nav?: string | null;
}

/*
 * Loose stand-in for the removed fabric.IEvent: the common payload shape of
 * the canvas events this engine subscribes to.
 */
export interface FabricEvent {
    e?: Event;
    target?: FabricObject;
    selected?: FabricObject[];
    viewportPoint?: Point;
}

export const EventTypes = {
    ObjectMoving: 'object:moving',
    ObjectScaling: 'object:scaling',
    ObjectRotating: 'object:rotating',
    ObjectSkewing: 'object:skewing',
    ObjectCreated: 'object:created',
    ObjectAdded: 'object:added',
    ObjectRemoved: 'object:removed',
    ObjectModified: 'object:modified',
    PathCreated: 'path:created',
    SelectionCreated: 'selection:created',
    SelectionUpdated: 'selection:updated',
    SelectionCleared: 'selection:cleared',
    MouseUp: 'mouse:up',
    MouseMove: 'mouse:move',
    MouseDown: 'mouse:down',
    MouseDoubleClick: 'mouse:dblclick',
    MouseOut: 'mouse:out',
    OrdinalPositionsChange: 'ordinalposition:change',
    TextEditingEntered: 'text:editing:entered',
    TextEditingExited: 'text:editing:exited'
};

export enum Tool {
    None,
    Select,
    Pencil,
    Rect,
    Text,
    Circle,
    Image,
    Video
}

export const ObjectTypes = {
    None: '',
    Rect: 'rect',
    Circle: 'circle',
    Text: 'i-text',
    Image: 'image',
    Video: 'video',
    ActiveSelection: 'activeSelection',
    Group: 'group',
    StickyNote: 'sticky-note',
    ResourceCard: 'resource-card',
    ProjectCard: 'project-card'
};

export const FontOptions = {
    Bold: 'bold',
    Italic: 'italic'
};

export type ObjectAlignment =
    | 'left'
    | 'centerX'
    | 'right'
    | 'top'
    | 'centerY'
    | 'bottom';

export type DistributeDirection = 'horizontal' | 'vertical';

export type ElementData = {
    type: string;
    version: string;
} & UnknownObject;

export interface VideoElement {
    id: string;
    element: HTMLVideoElement;
}
